import { PersonInfoApi } from "../api/Person";

export interface HighlightSegment {
  text: string;
  highlight: boolean;
}

/**
 * Extracts all name variations to highlight from person data
 * Returns: full names, first names, last names, and aliases
 */
export function extractNamesToHighlight(personEx: PersonInfoApi | undefined): {
  fullNames: string[];
  firstNames: string[];
  lastNames: string[];
} {
  if (!personEx?.names || personEx.names.length === 0) {
    return { fullNames: [], firstNames: [], lastNames: [] };
  }

  const fullNames: string[] = [];
  const firstNames: string[] = [];
  const lastNames: string[] = [];

  personEx.names.forEach((name) => {
    const firstName = name.first_name?.trim() || "";
    const lastName = name.last_name?.trim() || "";
    const middleName = name.middle_name?.trim() || "";

    // Build full name variations
    if (firstName && lastName) {
      // Full name: "First Last"
      fullNames.push(`${firstName} ${lastName}`);

      // If there's a middle name, also include "First Middle Last"
      if (middleName) {
        fullNames.push(`${firstName} ${middleName} ${lastName}`);
      }
    }

    // First name only (if it's a complete word, not a partial)
    if (firstName && firstName.length > 1) {
      firstNames.push(firstName);
    }

    // Last name only (if it's a complete word, not a partial)
    if (lastName && lastName.length > 1) {
      lastNames.push(lastName);
    }
  });

  // Remove duplicates
  return {
    fullNames: [...new Set(fullNames)],
    firstNames: [...new Set(firstNames)],
    lastNames: [...new Set(lastNames)],
  };
}

/**
 * Escapes special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Highlights candidate names in text while avoiding partial matches
 * Uses word boundaries to ensure we only match complete names
 */
export function highlightNamesInText(
  text: string,
  personEx: PersonInfoApi | undefined
): HighlightSegment[] {
  if (!text || !personEx) {
    return [{ text, highlight: false }];
  }

  const { fullNames, firstNames, lastNames } = extractNamesToHighlight(personEx);

  // Combine all names to match, prioritizing longer matches first
  const allNames = [
    ...fullNames,
    ...firstNames,
    ...lastNames,
  ].sort((a, b) => b.length - a.length); // Sort by length descending

  if (allNames.length === 0) {
    return [{ text, highlight: false }];
  }

  // Find all matches with their positions
  // We match each pattern separately to get all possible matches and handle overlaps correctly
  const allMatches: Array<{ start: number; end: number; text: string; length: number }> = [];

  allNames.forEach((name) => {
    const escaped = escapeRegex(name);
    // Use Unicode-aware word boundaries: match at start/end of string or before/after non-word characters
    // This handles accented characters like José, François, etc.
    const pattern = new RegExp(`(?<=^|[^\\p{L}\\p{N}_])${escaped}(?=[^\\p{L}\\p{N}_]|$)`, "giu");
    let match;

    while ((match = pattern.exec(text)) !== null) {
      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        length: match[0].length,
      });
    }
  });

  // Sort by start position, then by length (descending) to prioritize longer matches
  allMatches.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    return b.length - a.length;
  });

  // Remove overlapping matches, keeping longer matches
  const nonOverlappingMatches: Array<{ start: number; end: number; text: string }> = [];
  for (const match of allMatches) {
    // Find all overlapping matches
    const overlappingIndices: number[] = [];
    for (let i = 0; i < nonOverlappingMatches.length; i++) {
      const existing = nonOverlappingMatches[i];
      if (match.start < existing.end && match.end > existing.start) {
        overlappingIndices.push(i);
      }
    }

    if (overlappingIndices.length === 0) {
      // No overlap, add the match
      nonOverlappingMatches.push({
        start: match.start,
        end: match.end,
        text: match.text,
      });
    } else {
      // Check if this match is longer than all overlapping matches
      const matchLength = match.end - match.start;
      const isLongerThanAll = overlappingIndices.every(
        (idx) => matchLength > (nonOverlappingMatches[idx].end - nonOverlappingMatches[idx].start)
      );

      if (isLongerThanAll) {
        // Remove all overlapping matches (in reverse order to maintain indices)
        for (let i = overlappingIndices.length - 1; i >= 0; i--) {
          nonOverlappingMatches.splice(overlappingIndices[i], 1);
        }
        // Add the new longer match
        nonOverlappingMatches.push({
          start: match.start,
          end: match.end,
          text: match.text,
        });
      }
      // If not longer than all, skip this match
    }
  }

  // Sort final matches by start position
  nonOverlappingMatches.sort((a, b) => a.start - b.start);

  // Build segments
  const segments: HighlightSegment[] = [];
  let lastIndex = 0;

  nonOverlappingMatches.forEach((match) => {
    // Add unhighlighted text before match
    if (match.start > lastIndex) {
      segments.push({
        text: text.substring(lastIndex, match.start),
        highlight: false,
      });
    }

    // Add highlighted match
    segments.push({
      text: match.text,
      highlight: true,
    });

    lastIndex = match.end;
  });

  // Add remaining unhighlighted text
  if (lastIndex < text.length) {
    segments.push({
      text: text.substring(lastIndex),
      highlight: false,
    });
  }

  return segments.length > 0 ? segments : [{ text, highlight: false }];
}

